import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { FinanceService } from 'src/app/services/finance.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-payment-advice-print',
  standalone: true,
  imports: [CommonModule, MaterialModule, MatProgressBar],
  templateUrl: './payment-advice-print.component.html',
  styleUrl: './payment-advice-print.component.scss'
})
export class PaymentAdvicePrintComponent implements OnInit, OnDestroy {
  paymentId!: number;
  paymentData: any;
  previewData: any = null;
  pdfDoc: any;
  pdfSrc: string | null = null;
  isDialogMode = false;
  includeSignature = false;
  showVoucherSection = false;
  isGenerating = false;
  private generatedBlobUrl: string | null = null;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private financeService: FinanceService,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<PaymentAdvicePrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.paymentId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.paymentId) {
      this.loadPaymentAdviceData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
    if (this.generatedBlobUrl) {
      URL.revokeObjectURL(this.generatedBlobUrl);
    }
  }

  printPdf() {
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.pdfDoc?.download(`Payment-Advice-${this.previewData?.voucherNo || this.paymentId}.pdf`);
  }

  sendMail() {
    this.snackBar.open('Payment advice mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  onSignatureToggle(checked: boolean) {
    if (this.includeSignature === checked) {
      return;
    }

    this.includeSignature = checked;
    this.generatePdf();
  }

  onVoucherSectionToggle(checked: boolean) {
    if (this.showVoucherSection === checked) {
      return;
    }

    this.showVoucherSection = checked;
    this.generatePdf();
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadPaymentAdviceData(): void {
    this.financeService.getPaymentAdvicePrintById(this.paymentId).subscribe({
      next: (data: any) => {
        this.paymentData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.paymentData || {});
        this.generatePdf();
      },
      error: (err) => console.error('Error loading payment advice print', err)
    });
  }

  private buildPreviewData(source: any) {
    const header = source.header || {};
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const vendor = this.pickEntity(source, ['vendor', 'party', 'partydetails']);
    const branch = this.pickEntity(source, ['branch']);
    const rawSubdetails = this.asArray(source.voucherSubdetails);
    const isInvoiceLike = (row: any) => row?.ispurchasebill === true || row?.isexpense === true;
    const billRowsRaw = rawSubdetails.filter((row: any) => isInvoiceLike(row));
    const advanceRowsRaw = rawSubdetails.filter((row: any) => !isInvoiceLike(row));
    const deductionRowsRaw = billRowsRaw.flatMap((row: any) =>
      this.asArray(row.taxcharges).map((charge: any) => ({
        billNo: this.safeValue(row.billno, '-'),
        branchName: this.safeValue(row.branchname, '-'),
        ledgerName: this.safeValue(charge.ledgername || charge.ledger || charge.accountname, '-'),
        amount: Number(charge.amount || 0),
        remarks: this.safeValue(charge.remarks, '-')
      }))
    );
    const voucherDetailsRaw = this.asArray(source.voucherDetails);
    const summary = source.summary || {};

    const billRows = billRowsRaw.map((row: any, index: number) => {
      const otherDeduction = Number(row.otherdeductiontotal || this.sumAmounts(row.taxcharges, 'amount'));
      const invoiceSettlement = Number(row.amountpaid || 0);
      const tds = Number(row.tdsbillamount || row.tds || 0);
      const gross = Number(row.netbillamt || row.billamt || 0);
      const isTdsAlreadyDeducted = row?.istdsalreadydeducted === true;
      const paidAmount = Math.max(invoiceSettlement - (isTdsAlreadyDeducted ? 0 : tds), 0);

      return {
        sr: index + 1,
        branchName: this.safeValue(row.branchname, '-'),
        billNo: this.safeValue(row.billno, '-'),
        billDate: this.formatDateValue(row.billdate),
        partyInvNo: this.safeValue(row.partyinvno, '-'),
        partyInvDate: this.formatDateValue(row.partyinvdate),
        grossRaw: gross,
        invoiceSettlementRaw: invoiceSettlement,
        tdsRaw: tds,
        otherDeductionRaw: otherDeduction,
        paidAmountRaw: paidAmount,
        grossAmount: this.formatCurrency(gross, true),
        invoiceSettlementAmount: this.formatCurrency(invoiceSettlement, true),
        tdsAmount: this.formatCurrency(tds, true),
        otherDeductionAmount: this.formatCurrency(otherDeduction, true),
        paidAmount: this.formatCurrency(paidAmount, true)
      };
    });

    const deductionRows = deductionRowsRaw.map((row: any, index: number) => ({
      sr: index + 1,
      billNo: row.billNo,
      branchName: row.branchName,
      ledgerName: row.ledgerName,
      amount: this.formatCurrency(row.amount, true),
      remarks: row.remarks
    }));

    const voucherRows = voucherDetailsRaw.map((row: any, index: number) => ({
      sr: index + 1,
      ledgerName: this.safeValue(row.ledgername || row.accountname || row.name, '-'),
      debitAmount: this.formatCurrency(row.debitamount || 0, true),
      creditAmount: this.formatCurrency(row.creditamount || 0, true),
      remarks: this.safeValue(row.remarks, '-')
    }));

    const finalPaidAmount = Number(header.paidamount ?? summary.finalpaidamount ?? 0);
    const totalTds = Number(header.tdsamt ?? summary.totaltds ?? 0);
    const totalOtherDeductions = Number(summary.totalotherdeductions ?? this.sumRaw(deductionRowsRaw, 'amount'));
    const totalBillSettlement = Number(summary.totalbillsettlement ?? this.sumRaw(billRowsRaw, 'amountpaid'));
    const openAdvance = Number(summary.openadvance ?? header.openadvance ?? 0);
    const billGrossTotal = billRows.reduce((sum: number, row: any) => sum + row.grossRaw, 0);
    const billInvoiceSettlementTotal = billRows.reduce((sum: number, row: any) => sum + row.invoiceSettlementRaw, 0);
    const billTdsTotal = billRows.reduce((sum: number, row: any) => sum + row.tdsRaw, 0);
    const billOtherDeductionTotal = billRows.reduce((sum: number, row: any) => sum + row.otherDeductionRaw, 0);
    const billPaidAmountTotal = billRows.reduce((sum: number, row: any) => sum + row.paidAmountRaw, 0);
    const advanceTds = Math.max(totalTds - billTdsTotal, 0);
    const advanceNetRelease = Math.max(openAdvance - advanceTds, 0);
    const totalBillAmount = Number(summary.totalbillamount || 0);
    const totalTaxableAmount = Number(summary.totaltaxableamount || 0);
    const totalTaxAmount = Number(summary.totaltaxamount || 0);
    const roundOffAmount = Number(summary.roundoffamount ?? header.roundoffamount ?? 0);
    const advanceSectionRows = openAdvance > 0 || advanceTds > 0
      ? [{
          sr: 1,
          description: 'Advance Adjustment',
          advanceAmount: this.formatCurrency(openAdvance, true),
          advanceTds: this.formatCurrency(advanceTds, true),
          advanceRelease: this.formatCurrency(advanceNetRelease, true)
        }]
      : [];
    const billSummaryRows = [
      { label: 'Bill Gross', value: billGrossTotal },
      { label: 'Invoice Settel Amount', value: billInvoiceSettlementTotal },
      { label: 'Bill TDS', value: billTdsTotal, show: billTdsTotal > 0 },
      { label: 'Bill Other Ded.', value: billOtherDeductionTotal, show: billOtherDeductionTotal > 0 },
      { label: 'Paid Amount', value: billPaidAmountTotal }
    ].filter((row: any) => row.show !== false)
      .map((row: any) => ({ ...row, formattedValue: this.formatCurrency(row.value, true) }));

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyNameTitle: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      voucherNo: this.safeValue(header.voucherno || header.code),
      voucherDate: this.formatDateValue(header.voucherdate),
      paymentMode: this.safeValue(header.paymentmodename || header.paymentmode || header.paymentmodeid, '-'),
      paymentType: this.safeValue(header.paymentvouchertypename || header.paymentvouchertype || header.paymentvouchertypeid, '-'),
      chequeNo: this.safeValue(header.chequeno, '-'),
      chequeDate: this.formatDateValue(header.chequedate),
      vendorName: this.safeValue(vendor.name || vendor.vendorname, '-'),
      vendorAddress: this.joinAddress([vendor.address1, vendor.address2, vendor.city, vendor.state, vendor.country, vendor.pincode]),
      vendorMeta: [
        this.metaLine('GSTIN', vendor.gstin || vendor.gstnumber),
        this.metaLine('PAN', vendor.pan || vendor.panno),
        this.metaLine('Phone', vendor.mobile || vendor.phone),
        this.metaLine('Email', vendor.email || vendor.emailid)
      ].filter(Boolean),
      branchName: this.safeValue(branch.branchname, '-'),
      bankName: this.safeValue(header.paytobankname, '-'),
      bankAccountNo: this.safeValue(header.paytobankaccountnumber, '-'),
      bankIfsc: this.safeValue(header.paytobankifsccode, '-'),
      bankBranch: this.safeValue(header.paytobankbranch, '-'),
      overview: [
        { label: 'Vendor', value: this.safeValue(vendor.name || vendor.vendorname, '-') },
        { label: 'Payment Mode', value: this.safeValue(header.paymentmodename || header.paymentmode || header.paymentmodeid, '-') },
        { label: 'Total TDS', value: this.formatCurrency(totalTds, true) },
        { label: 'Net Release', value: this.formatCurrency(finalPaidAmount, true) }
      ],
      hasBillRows: billRows.length > 0,
      hasAdvanceRows: advanceRowsRaw.length > 0,
      hasAdvanceSection: advanceSectionRows.length > 0,
      hasDeductionRows: deductionRows.length > 0,
      hasVoucherRows: voucherRows.length > 0,
      billRows,
      advanceSectionRows,
      deductionRows,
      voucherRows,
      billSummaryRows,
      advanceTds: this.formatCurrency(advanceTds, true),
      advanceNetRelease: this.formatCurrency(advanceNetRelease, true),
      totalBillAmount: this.formatCurrency(totalBillAmount, true),
      totalTaxableAmount: this.formatCurrency(totalTaxableAmount, true),
      totalTaxAmount: this.formatCurrency(totalTaxAmount, true),
      totalBillSettlement: this.formatCurrency(totalBillSettlement, true),
      totalTds: this.formatCurrency(totalTds, true),
      totalOtherDeductions: this.formatCurrency(totalOtherDeductions, true),
      openAdvance: this.formatCurrency(openAdvance, true),
      roundOffAmount: this.formatCurrency(roundOffAmount, true),
      finalPaidAmount: this.formatCurrency(finalPaidAmount, true),
      amountInWords: `${toWords.convert(finalPaidAmount, { currency: true })}/-`,
      remarks: this.safeValue(header.remarks, '-')
    };
  }

  private generatePdf() {
    if (!this.previewData) {
      return;
    }

    const billBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'BRANCH', style: 'tableHeader' },
        { text: 'BILL NO', style: 'tableHeader' },
        { text: 'BILL DATE', style: 'tableHeader' },
        { text: 'PARTY INV', style: 'tableHeader' },
        { text: 'GROSS', style: 'tableHeader' },
        { text: 'INVOICE SETTEL AMOUNT', style: 'tableHeader' },
        { text: 'TDS', style: 'tableHeader' },
        { text: 'OTHER DED.', style: 'tableHeader' },
        { text: 'PAID AMOUNT', style: 'tableHeader' }
      ],
      ...this.previewData.billRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.branchName },
        { text: row.billNo },
        { text: row.billDate, alignment: 'center' },
        {
          stack: [
            { text: row.partyInvNo },
            { text: row.partyInvDate, fontSize: 8, color: '#64748b' }
          ]
        },
        { text: row.grossAmount, alignment: 'right' },
        { text: row.invoiceSettlementAmount, alignment: 'right' },
        { text: row.tdsAmount, alignment: 'right' },
        { text: row.otherDeductionAmount, alignment: 'right' },
        { text: row.paidAmount, alignment: 'right', bold: true }
      ])),
      ...(this.previewData.billSummaryRows.length ? [[
        { text: 'BILL SUMMARY', colSpan: 5, alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 4, 8, 4] },
        {}, {}, {}, {},
        { text: this.previewData.billSummaryRows.find((x: any) => x.label === 'Bill Gross')?.formattedValue || '-', alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billSummaryRows.find((x: any) => x.label === 'Invoice Settel Amount')?.formattedValue || '-', alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billSummaryRows.find((x: any) => x.label === 'Bill TDS')?.formattedValue || '-', alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billSummaryRows.find((x: any) => x.label === 'Bill Other Ded.')?.formattedValue || '-', alignment: 'right', fillColor: '#eef2ff', bold: true },
        { text: this.previewData.billSummaryRows.find((x: any) => x.label === 'Paid Amount')?.formattedValue || '-', alignment: 'right', fillColor: '#eef2ff', bold: true }
      ]] : [])
    ];

    const advanceBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'DESCRIPTION', style: 'tableHeader' },
        { text: 'ADVANCE AMOUNT', style: 'tableHeader' },
        { text: 'ADVANCE TDS', style: 'tableHeader' },
        { text: 'ADVANCE RELEASE', style: 'tableHeader' }
      ],
      ...this.previewData.advanceSectionRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.description },
        { text: row.advanceAmount, alignment: 'right' },
        { text: row.advanceTds, alignment: 'right' },
        { text: row.advanceRelease, alignment: 'right', bold: true }
      ]))
    ];

    const deductionBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'BILL NO', style: 'tableHeader' },
        { text: 'BRANCH', style: 'tableHeader' },
        { text: 'LEDGER', style: 'tableHeader' },
        { text: 'AMOUNT', style: 'tableHeader' },
        { text: 'REMARKS', style: 'tableHeader' }
      ],
      ...this.previewData.deductionRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.billNo },
        { text: row.branchName },
        { text: row.ledgerName },
        { text: row.amount, alignment: 'right', bold: true },
        { text: row.remarks }
      ]))
    ];

    const voucherBody = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'LEDGER', style: 'tableHeader' },
        { text: 'DEBIT', style: 'tableHeader' },
        { text: 'CREDIT', style: 'tableHeader' },
        { text: 'REMARKS', style: 'tableHeader' }
      ],
      ...this.previewData.voucherRows.map((row: any) => ([
        { text: String(row.sr), alignment: 'center' },
        { text: row.ledgerName },
        { text: row.debitAmount, alignment: 'right' },
        { text: row.creditAmount, alignment: 'right' },
        { text: row.remarks }
      ]))
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 12, 20, 42],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              { width: '*', text: 'Generated using VCS ERP', style: 'footerText' },
              { width: 90, text: `Page ${currentPage} of ${pageCount}`, style: 'footerPageText', alignment: 'right' }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? { image: this.previewData.logo, alignment: 'center', width: 300, height: 300, opacity: 0.05, marginTop: 220 }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [74, '*', 165],
            body: [[
              {
                stack: this.previewData.logo
                  ? [{ image: this.previewData.logo, width: 66, fit: [66, 66], alignment: 'center' }]
                  : [{ text: this.previewData.companyInitials, style: 'logoFallback' }],
                border: [false, false, false, false],
                margin: [0, 6, 0, 0]
              },
              {
                stack: [
                  { text: this.previewData.companyName.toUpperCase(), style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((text: string) => ({ text, style: 'companyMeta' }))
                ].filter(Boolean),
                border: [false, false, false, false],
                margin: [0, 4, 0, 0]
              },
              {
                stack: [
                  { text: 'PAYMENT ADVICE', style: 'invoiceHeaderBadge' },
                  { text: `Advice No: ${this.previewData.voucherNo}`, style: 'invoiceMetaStrong' },
                  { text: `Advice Date: ${this.previewData.voucherDate}`, style: 'invoiceMeta' },
                  { text: `Pay Type: ${this.previewData.paymentType}`, style: 'invoiceMeta' },
                  { text: `Mode: ${this.previewData.paymentMode}`, style: 'invoiceMeta' }
                ],
                fillColor: '#f6f8ff',
                margin: [10, 8, 10, 8],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: this.previewData.overview.map((card: any) => ({
            width: '*',
            stack: [
              { text: card.label, style: 'cardTitle' },
              { text: card.value, style: 'cardHeading' }
            ],
            margin: [8, 8, 8, 8],
            fillColor: '#f8fafc'
          })),
          columnGap: 12,
          margin: [0, 10, 0, 8]
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Payee / Vendor', style: 'cardTitle' },
                { text: this.previewData.vendorName, style: 'cardHeading' },
                ...(this.previewData.vendorAddress ? [{ text: this.previewData.vendorAddress, style: 'cardText' }] : []),
                ...this.previewData.vendorMeta.map((text: string) => ({ text, style: 'cardText' }))
              ],
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            },
            {
              width: '*',
              stack: [
                { text: 'Payment Instructions', style: 'cardTitle' },
                { text: `Branch: ${this.previewData.branchName}`, style: 'cardText' },
                { text: `Bank: ${this.previewData.bankName}`, style: 'cardText' },
                { text: `A/C No: ${this.previewData.bankAccountNo}`, style: 'cardText' },
                { text: `IFSC: ${this.previewData.bankIfsc}`, style: 'cardText' },
                { text: `Bank Branch: ${this.previewData.bankBranch}`, style: 'cardText' },
                { text: `Cheque No: ${this.previewData.chequeNo}`, style: 'cardText' },
                { text: `Cheque Date: ${this.previewData.chequeDate}`, style: 'cardText' }
              ],
              margin: [8, 8, 8, 8],
              fillColor: '#f8fafc'
            }
          ],
          columnGap: 14,
          margin: [0, 0, 0, 8]
        },
        ...(this.previewData.hasBillRows ? [
          { text: 'Bill-wise Payment Advice', style: 'sectionHeader' },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [22, 48, 48, 42, 68, 48, 64, 40, 48, 56],
              body: billBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        ...(this.previewData.hasAdvanceSection ? [
          { text: 'Advance Summary', style: 'sectionHeader', margin: [0, 10, 0, 0] },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [24, '*', 82, 74, 86],
              body: advanceBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        ...(this.previewData.hasDeductionRows ? [
          { text: 'Other Deductions / Settlement Adjustments', style: 'sectionHeader', margin: [0, 10, 0, 0] },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [24, 58, 54, 118, 56, '*'],
              body: deductionBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        ...(this.showVoucherSection && this.previewData.hasVoucherRows ? [
          { text: 'Voucher Ledger Summary', style: 'sectionHeader', margin: [0, 10, 0, 0] },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: [24, '*', 68, 68, 110],
              body: voucherBody
            },
            layout: this.buildTableLayout()
          }
        ] : []),
        {
          margin: [0, 10, 0, 0],
          columns: [
            {
              width: '*',
              stack: [
                { text: 'Amount In Words', style: 'sectionHeader' },
                { text: this.previewData.amountInWords, style: 'cardText' },
                ...(this.previewData.remarks !== '-' ? [
                  { text: 'Remarks', style: 'sectionHeader', margin: [0, 12, 0, 0] },
                  { text: this.previewData.remarks, style: 'cardText' }
                ] : [])
              ]
            },
            {
              width: 188,
              unbreakable: true,
              stack: [
                {
                  margin: [0, 50, 0, 0],
                  unbreakable: true,
                  stack: [
                    { text: `For ${this.previewData.companyNameTitle}`, alignment: 'right', bold: true, color: '#153a6b' },
                    ...(this.includeSignature
                      ? [{
                          columns: [
                            { width: '*', text: '' },
                            {
                              width: 'auto',
                              stack: [
                                {
                                  columns: [
                                    ...(this.previewData.stampImage ? [{ width: 70, image: this.previewData.stampImage, fit: [70, 70], margin: [0, 4, 6, 0] }] : []),
                                    ...(this.previewData.signatureImage ? [{ width: 74, image: this.previewData.signatureImage, fit: [74, 34], margin: [0, 22, 0, 0] }] : [])
                                  ]
                                }
                              ]
                            }
                          ],
                          margin: [0, 8, 0, 0]
                        }]
                      : []),
                    { text: this.previewData.signatoryName || '', alignment: 'right', margin: [0, 12, 0, 0], fontSize: 8.6, bold: true },
                    { text: this.previewData.signatoryTitle || 'Authorized Signatory', alignment: 'right', fontSize: 8.6, color: '#475569' }
                  ]
                }
              ]
            }
          ]
        }
      ],
      styles: {
        companyHeader: { fontSize: 18, bold: true, color: '#0f2852', alignment: 'center' },
        companyMeta: { fontSize: 10.2, color: '#2b3e5f', alignment: 'center', margin: [0, 1, 0, 0] },
        invoiceHeaderBadge: { fontSize: 11, bold: true, color: '#0f2852', margin: [0, 0, 0, 8] },
        invoiceMetaStrong: { fontSize: 9.4, bold: true, color: '#12253f', margin: [0, 2, 0, 0] },
        invoiceMeta: { fontSize: 9, color: '#334155', margin: [0, 2, 0, 0] },
        cardTitle: { fontSize: 9, bold: true, color: '#48627f', margin: [0, 0, 0, 6] },
        cardHeading: { fontSize: 12, bold: true, color: '#0f2852' },
        cardText: { fontSize: 9.1, color: '#334155', margin: [0, 2, 0, 0] },
        tableHeader: { fontSize: 8.7, bold: true, color: '#ffffff', fillColor: '#17354d', alignment: 'center', margin: [0, 4, 0, 4] },
        sectionHeader: { fontSize: 11.2, bold: true, color: '#0b7285', margin: [0, 0, 0, 6] },
        summaryLabel: { fontSize: 9, color: '#334155', margin: [0, 4, 0, 4] },
        summaryValue: { fontSize: 9, color: '#0f172a', alignment: 'right', margin: [0, 4, 0, 4], bold: true },
        summaryTotalLabel: { fontSize: 9.5, color: '#0f2852', bold: true, fillColor: '#eef2ff', margin: [0, 5, 0, 5] },
        summaryTotalValue: { fontSize: 9.5, color: '#0f2852', alignment: 'right', bold: true, fillColor: '#eef2ff', margin: [0, 5, 0, 5] },
        footerText: { fontSize: 8, color: '#64748b' },
        footerPageText: { fontSize: 8, color: '#334155', bold: true },
        logoFallback: {
          fontSize: 26,
          bold: true,
          color: '#153a6b',
          alignment: 'center',
          margin: [0, 16, 0, 0]
        }
      },
      defaultStyle: {
        fontSize: 9,
        color: '#0f172a'
      }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private buildTableLayout() {
    return {
      hLineColor: () => '#b9c7d8',
      vLineColor: () => '#b9c7d8',
      hLineWidth: () => 0.7,
      vLineWidth: () => 0.7,
      fillColor: (rowIndex: number) => rowIndex === 0 ? '#17354d' : rowIndex % 2 === 0 ? '#f8fafc' : null
    };
  }

  getBillSummaryValue(label: string): string {
    const row = this.previewData?.billSummaryRows?.find((item: any) => item.label === label);
    return row?.formattedValue || '-';
  }

  private buildMiniSummaryLayout() {
    return {
      hLineColor: () => '#d4dce6',
      vLineColor: () => '#d4dce6',
      hLineWidth: () => 0.6,
      vLineWidth: () => 0.6,
      fillColor: (rowIndex: number) => rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff'
    };
  }

  private pickEntity(source: any, keys: string[]) {
    for (const key of keys) {
      if (source?.[key]) {
        return source[key];
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    const gstLine = this.metaLine('GSTIN', company.gstin || company.gstnumber, '');
    const panLine = this.metaLine('PAN', company.pan || company.panno, '');
    const emailLine = this.metaLine('Email', company.email || company.emailid, '');
    const websiteLine = this.metaLine('Website', company.website, '');

    return [
      [gstLine, panLine].filter(Boolean).join('    '),
      [emailLine, websiteLine].filter(Boolean).join('    ')
    ].filter(Boolean);
  }

  private metaLine(label: string, value: any, fallback = ''): string {
    const cleaned = this.safeValue(value, '');
    return cleaned ? `${label}: ${cleaned}` : fallback;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value, '-');
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private formatCurrency(value: any, showSymbol = true): string {
    const amount = Number(value || 0);
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return showSymbol ? `₹ ${formatter.format(amount)}` : formatter.format(amount);
  }

  private formatNumber(value: any): string {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(Number(value || 0));
  }

  private joinAddress(parts: any[]): string {
    return parts
      .map((part) => this.safeValue(part, ''))
      .filter(Boolean)
      .join(', ');
  }

  private getInitials(name: string): string {
    return this.safeValue(name, 'VC')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || '')
      .join('');
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'string') {
        continue;
      }

      const trimmed = candidate.trim();
      if (!trimmed) {
        continue;
      }

      if (trimmed.startsWith('data:image')) {
        return trimmed;
      }

      return `data:image/png;base64,${trimmed}`;
    }

    return null;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined) {
      return fallback;
    }

    const text = String(value).trim();
    return text ? text : fallback;
  }

  private asArray(value: any): any[] {
    return Array.isArray(value) ? value : [];
  }

  private sumAmounts(rows: any, key: string): number {
    return this.asArray(rows).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
  }

  private sumRaw(rows: any[], key: string): number {
    return this.asArray(rows).reduce((sum, row) => sum + Number(row?.[key] || 0), 0);
  }
}
